import { useAppSelector } from '@casops/store/hooks';
import { CustomCellRendererProps } from 'ag-grid-react';
import { ReactElement } from 'react';
import { SidePanel } from '@casops/store/slices/globalSlice';
import CellActions from '../cell-actions/CellActions';
import { ModelName } from '@casops/store/api';
import { Permission, userHasPermission } from '@casops/permissions';
import { ModelWithId } from '@casops/store/apis/commonModels';
import EditAction from './components/edit-action/EditAction';
import DeleteAction from './components/delete-action/DeleteAction';
import { UseMutationHook } from '@casops/store/types';
import DownloadAction from './components/download-action/DownloadAction';
import UnlockAction from './components/unlock-action/UnlockAction';

export type ActionItemCallback<TData extends ModelWithId> = (data: TData) => boolean;

export interface ModelEditProps<TData extends ModelWithId> {
  sidePanel: SidePanel;
  canEditItem: boolean | ActionItemCallback<TData>;
}

export interface ModelDeleteProps<TData extends ModelWithId, TQueryArg> {
  canDeleteItem: boolean | ActionItemCallback<TData>;
  getItemName: (data: TData) => string;
  getQueryArg: (data: TData) => TQueryArg;
  useDeleteModelMutation: UseMutationHook<TQueryArg>;
}

export interface ModelUnlockProps<TData extends ModelWithId, TQueryArg> {
  canUnlockItem: boolean | ActionItemCallback<TData>;
  getItemName: (data: TData) => string;
  getQueryArg: (data: TData) => TQueryArg;
  useUnlockModelMutation: UseMutationHook<TQueryArg>;
}

export interface ModelDownloadProps<TData extends ModelWithId> {
  showDownloadItem: boolean | ActionItemCallback<TData>; // Enables row-level control of action visibility.
  canDownloadItem: boolean | ActionItemCallback<TData>;
  getDownloadUrl: (data: TData) => string;
}

export interface ModelActionsCellRendererParams<
  TData extends ModelWithId,
  TQueryArg = ModelWithId,
> {
  modelName: ModelName;
  permission: Permission;
  unlockProps?: ModelUnlockProps<TData, TQueryArg>; // Opt-in unlock user action.
  editProps?: ModelEditProps<TData>; // Opt-in edit action.
  deleteProps?: ModelDeleteProps<TData, TQueryArg>; // Opt-in delete action.
  downloadProps?: ModelDownloadProps<TData>; // Opt-in download action.
}

export const ModelActionsCellRenderer = <TData extends ModelWithId, TQueryArg = ModelWithId>({
  data,
  modelName,
  permission,
  unlockProps,
  editProps,
  deleteProps,
  downloadProps,
}: CustomCellRendererProps<TData> &
  ModelActionsCellRendererParams<TData, TQueryArg>): ReactElement | null => {
  const user = useAppSelector(state => state.auth);

  // Only show the actions if the user has the required permission.
  const showActions = userHasPermission(user, permission);

  if (typeof data !== 'undefined' && showActions) {
    return (
      <CellActions wide>
        {downloadProps && <DownloadAction<TData> data={data} {...downloadProps} />}
        {unlockProps && (
          <UnlockAction<TData, TQueryArg> data={data} modelName={modelName} {...unlockProps} />
        )}
        {editProps && <EditAction<TData> data={data} {...editProps} />}
        {deleteProps && (
          <DeleteAction<TData, TQueryArg> data={data} modelName={modelName} {...deleteProps} />
        )}
      </CellActions>
    );
  }

  return null;
};

export default ModelActionsCellRenderer;
